import moment from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';

export const SPECIES_LABELS = { dog: 'Perro', cat: 'Gato' };

export const OTHERS_LABEL = 'Otros';

// Orden y labels de las líneas de negocio; el contrato de backend los deja al front.
export const BUSINESS_LINES = [
  { key: 'b2c', label: 'B2C' },
  { key: 'b2b2c', label: 'B2B2C' },
  { key: 'employeeBenefit', label: 'Employee Benefit' },
  { key: 'assistance', label: 'Asistencia' },
  { key: 'other', label: 'Otro' },
];

// Siempre las 5 claves: un backend sin el campo o con claves faltantes queda en 0.
export function normalizeBusinessLine(counts) {
  const source = counts || {};
  return BUSINESS_LINES.reduce((acc, { key }) => ({ ...acc, [key]: Number(source[key]) || 0 }), {});
}

export const EMPTY_INSIGHTS = Object.freeze({
  range: null,
  totals: {
    attendances: 0,
    uniquePets: 0,
    uniqueTutors: 0,
    recurringPets: 0,
    invoicedTotal: 0,
    pawerCoverage: 0,
    paidToClinics: 0,
    pendingToClinics: 0,
    userPayment: 0,
    avgTicket: null,
    coverageRate: null,
    historicalAttendances: null,
  },
  byBusinessLine: normalizeBusinessLine(null),
  byClinic: [],
  byPawerService: [],
  byClinicService: [],
  bySpecies: [],
});

// Días del rango contando ambos extremos. Sin fromDate ("Histórico") no hay
// forma de saber el largo, así que siempre se agrupa por mes.
export function granularityFor(fromDate, toDate) {
  if (!fromDate) return 'month';
  const days = moment(toDate, DATE_FORMAT).diff(moment(fromDate, DATE_FORMAT), 'days') + 1;
  if (days <= 31) return 'day';
  if (days <= 92) return 'week';
  return 'month';
}

export function presetRange(preset, today = moment()) {
  const base = moment(today).startOf('day');
  const toDate = base.format(DATE_FORMAT);
  switch (preset) {
    case 'previousMonth': {
      const prev = base.clone().subtract(1, 'month');
      return {
        fromDate: prev.clone().startOf('month').format(DATE_FORMAT),
        toDate: prev.clone().endOf('month').format(DATE_FORMAT),
      };
    }
    case 'last3Months':
      return { fromDate: base.clone().subtract(3, 'months').add(1, 'day').format(DATE_FORMAT), toDate };
    case 'historical':
      return { fromDate: null, toDate };
    case 'currentMonth':
    default:
      return { fromDate: base.clone().startOf('month').format(DATE_FORMAT), toDate };
  }
}

const share = (value, total) => (total > 0 ? (value / total) * 100 : 0);

// Top n de un ranking ya ordenado; el resto se suma en una fila "Otros" para
// que las barras cuadren con el total.
export function topWithOthers(list, n, countKey, extraKeys = []) {
  const rows = list || [];
  const total = rows.reduce((sum, row) => sum + (row[countKey] || 0), 0);
  const top = rows.slice(0, n).map((row) => ({ ...row, percentage: share(row[countKey] || 0, total) }));
  const rest = rows.slice(n);
  if (rest.length === 0) return top;
  const others = { title: OTHERS_LABEL, isOthers: true, [countKey]: 0 };
  extraKeys.forEach((key) => { others[key] = 0; });
  rest.forEach((row) => {
    others[countKey] += row[countKey] || 0;
    extraKeys.forEach((key) => { others[key] += row[key] || 0; });
  });
  others.percentage = share(others[countKey], total);
  return [...top, others];
}

export function speciesShare(bySpecies) {
  const rows = bySpecies || [];
  const total = rows.reduce((sum, row) => sum + row.attendances, 0);
  return rows.map((row) => ({
    key: row.species,
    label: SPECIES_LABELS[row.species] || row.species,
    attendances: row.attendances,
    percentage: share(row.attendances, total),
  }));
}

// % sobre la suma de las líneas (== totals.attendances por contrato), así cuadra con la barra.
export function businessLineShare(counts) {
  const normalized = normalizeBusinessLine(counts);
  const total = BUSINESS_LINES.reduce((sum, { key }) => sum + normalized[key], 0);
  return BUSINESS_LINES.map(({ key, label }) => ({
    key,
    label,
    attendances: normalized[key],
    percentage: share(normalized[key], total),
  }));
}

// 1 decimal, pero sin ".0" cuando el valor redondeado es entero: 50 → "50%", 15.25 → "15.3%".
export function shareLabel(percentage) {
  return `${Number(percentage.toFixed(1))}%`;
}

// Totales sobre TODAS las clínicas filtradas, no sobre la página visible.
export function clinicTotals(byClinic) {
  return (byClinic || []).reduce(
    (acc, c) => {
      const lines = normalizeBusinessLine(c.byBusinessLine);
      return {
        attendances: acc.attendances + (c.attendances || 0),
        pawerCoverage: acc.pawerCoverage + (c.pawerCoverage || 0),
        paid: acc.paid + (c.paid?.amount || 0),
        pending: acc.pending + (c.pending?.amount || 0),
        userPayment: acc.userPayment + (c.userPayment || 0),
        byBusinessLine: BUSINESS_LINES.reduce(
          (sum, { key }) => ({ ...sum, [key]: acc.byBusinessLine[key] + lines[key] }),
          {}
        ),
      };
    },
    {
      attendances: 0, pawerCoverage: 0, paid: 0, pending: 0, userPayment: 0,
      byBusinessLine: normalizeBusinessLine(null),
    }
  );
}

export function rangeLabel(range) {
  if (!range || !range.fromDate) return '';
  return `Desde el ${moment(range.fromDate, DATE_FORMAT).format('DD-MM-YYYY')}`;
}

// Línea chica del badge "Total de atenciones" (025): total global hasta hoy, con la
// fecha de inicio solo en el preset Histórico. Sin número (backend viejo) → ''.
export function historicalAttendancesLabel(count, range, withRange) {
  if (!Number.isFinite(count)) return '';
  const noun = count === 1 ? 'histórica' : 'históricas';
  const label = `${new Intl.NumberFormat('es-CL').format(count)} ${noun}`;
  if (!withRange || !range || !range.fromDate) return label;
  return `${label} · desde el ${moment(range.fromDate, DATE_FORMAT).format('DD-MM-YYYY')}`;
}

export function pct(value) {
  return value == null ? '—' : `${Number(value).toFixed(1)}%`;
}
